import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Link,
  Alert,
  CircularProgress,
} from "@mui/material";

import { LockOutlined } from "@mui/icons-material";

import { resetPassword } from "../api/authApi";

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!password || !confirmPassword) {
      setError("Please fill in both fields.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Password and confirm password do not match.");
      return;
    }

    try {
      setLoading(true);
      const data = await resetPassword(token, password, confirmPassword);
      setSuccess(data.message);
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to reset your password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #eef6ff 0%, #f5fbfa 50%, #ffffff 100%)",
        px: 1,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: "100%",
          maxWidth: 380,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
        }}
      >
        <Typography variant="h6" fontWeight={600} textAlign="center" gutterBottom>
          Set a new password
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 1 }}>
            {success}
          </Alert>
        )}

        {!success && (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              size="small"
              label="New Password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              margin="normal"
              placeholder="Enter your new password"
              sx={{
                mb: 0,
                "& input::placeholder": { fontSize: "14px" },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ fontSize: 18, color: "lightgrey" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              margin="normal"
              placeholder="Confirm your new password"
              sx={{
                mb: 0,
                "& input::placeholder": { fontSize: "14px" },
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ fontSize: 18, color: "lightgrey" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ height: 38, borderRadius: 2, textTransform: "none", mt: 2 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Reset password"}
            </Button>
          </Box>
        )}

        <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mt: 3 }}>
          <Link href="/" underline="hover" fontWeight={600}>
            Back to sign in
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default ResetPassword;